
import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, AlertTriangle, Loader2 } from 'lucide-react';
import { inviteFormSchema, InviteFormValues } from './useInvites';

interface InviteFormDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  isLoading: boolean;
  handleCreateInvite: (values: InviteFormValues) => Promise<void>;
}

const InviteFormDialog: React.FC<InviteFormDialogProps> = ({
  isOpen,
  setIsOpen,
  isLoading,
  handleCreateInvite
}) => {
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inviteForm = useForm<InviteFormValues>({
    resolver: zodResolver(inviteFormSchema),
    defaultValues: {
      email: "",
      makeAdmin: false
    }
  });

  const onSubmit = async (values: InviteFormValues) => {
    setFormError(null);
    setIsSubmitting(true);
    try {
      await handleCreateInvite(values);
      inviteForm.reset(); // Reset form on success
    } catch (error: any) {
      setFormError(error.message || 'An error occurred while creating the invite');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) {
        // Reset form and errors when dialog closes
        inviteForm.reset();
        setFormError(null);
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="default" className="ml-auto">
          <Mail className="mr-2 h-4 w-4" />
          Create Invite
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Send New Invite</DialogTitle>
          <DialogDescription>
            Create an invite link that will be valid for 7 days
          </DialogDescription>
        </DialogHeader>
        
        {formError && (
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        
        <Form {...inviteForm}>
          <form onSubmit={inviteForm.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={inviteForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter recipient email" 
                      {...field} 
                      disabled={isSubmitting || isLoading}
                    />
                  </FormControl>
                  <FormDescription>
                    An invite link will be sent to this email address
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={inviteForm.control}
              name="makeAdmin"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isSubmitting || isLoading}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Make Admin</FormLabel>
                    <FormDescription>
                      Grant administrator privileges to this user
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button 
                type="submit" 
                disabled={isSubmitting || isLoading}
                className="w-full sm:w-auto"
              >
                {(isSubmitting || isLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isSubmitting ? 'Sending...' : 'Send Invite'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteFormDialog;
